import { Api, ApiError } from "../api";
import { reload } from "../nav";
import { h, label, pill, replace } from "../dom";
import { longDate, money, qty, shortDate, unitPrice, when } from "../format";
import { logRow, sentEmailFor } from "../mail";
import type { ActResult, Draft, FullOrder, HistoryEntry, Me, OrderHistory, OutboundLogRow, Page } from "../types";
import { forVendorPo } from "../history";
import { viewMode } from "../orders";
import { historySection } from "./history";
import { can, confirmButton, crumbs, modeNotice, openPdf, orderCrumbs, outcome, prepareEmailButton } from "./shared";
import { plain } from "../words";

// One vendor PO awaiting approval (the "Approval" screen): what we're buying at what cost against the customer's
// price, the PDF, and Approve (Josh only). Approving doesn't send it: it prepares the email to the supplier, with the
// PDF attached, as a draft a person sends.

/** What went to the supplier: when, to whom, who actually got it (in SIMULATION, the simulation recipients), the attachments. */
function sentPanel(d: Draft, log: OutboundLogRow | undefined, supplier: string): HTMLElement {
  const final = d.final ?? { to: d.to, cc: d.cc, subject: d.subject };
  return h("div", { class: "panel facts" },
    label(`Email to ${supplier}`),
    h("div", null, pill(log?.decision === "REDIRECTED" ? "sent to simulation recipients" : "sent", "good")),
    h("span", null, `${when(log?.at ?? d.reviewedAt)}${d.reviewedBy ? ` by ${d.reviewedBy}` : ""}`),
    label("To"), h("span", null, (final.to ?? d.to).join(", ")),
    (final.cc ?? d.cc).length ? [label("Cc"), h("span", null, (final.cc ?? d.cc).join(", "))] : null,
    log?.decision === "REDIRECTED"
      ? [label("Delivered to"), h("span", null, log.deliveredTo.join(", ")), h("span", { class: "caption" }, "Simulation: only these got it, marked [SIM].")]
      : null,
    (log?.attachments ?? d.attachments).length ? [label("Attached"), h("span", { class: "num" }, (log?.attachments ?? d.attachments).join(", "))] : null,
    h("a", { class: "button", href: `/draft/${d.draftId}` }, "Open the email"));
}

const EMAIL_STATE: Record<string, [string, "good" | "soon" | "neutral"]> = {
  PENDING: ["waiting to be sent", "soon"], SENT: ["sent", "good"], DISCARDED: ["discarded", "neutral"],
};

/** Every email for the PO, newest first, each with its state (waiting, sent, discarded) and a link. */
function emailsPanel(emails: Draft[]): HTMLElement {
  const sorted = [...emails].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  return h("div", { class: "panel facts" },
    label(sorted.length === 1 ? "Its email" : `Its emails (${sorted.length})`),
    h("ul", { class: "plain" }, sorted.map((d) => {
      const [state, tone] = EMAIL_STATE[d.status] ?? [d.status.toLowerCase(), "neutral" as const];
      return h("li", { class: "row-act" },
        pill(state, tone),
        h("a", { href: `/draft/${d.draftId}` }, `Email ${d.draftId}`),
        h("span", { class: "caption" }, when(d.reviewedAt ?? d.createdAt)));
    })));
}

/**
 * A vendor PO by its number (an email knows only that): its order comes from order_history, then the screen as usual.
 */
export async function vendorPoByNumber(api: Api, me: Me, number: string): Promise<HTMLElement> {
  const found = await api.tool<OrderHistory>("order_history", { vendor_po: number, limit: 1 }).catch(() => null);
  if (!found?.customerPoId) return h("div", { class: "page" }, h("p", { class: "lede" }, `There's no vendor PO ${number}.`));
  return vendorPo(api, me, found.customerPoId, number);
}

/** One vendor PO, by its id or its number, on its order. */
export async function vendorPo(api: Api, me: Me, customerPoId: number, vendorPoId: number | string,
                               query = new URLSearchParams()): Promise<HTMLElement> {
  const view = viewMode(query, me.mode);
  const [order, orderHistory] = await Promise.all([
    api.tool<FullOrder>("get_order", { customer_po_id: customerPoId, ...(view.other ? { mode: view.mode } : {}) }),
    api.tool<OrderHistory>("order_history", { customer_po_id: customerPoId }).then((r) => r.history ?? [], () => [] as HistoryEntry[]),
  ]);
  const v = order.vendorPos.find((x) => (typeof vendorPoId === "number" ? x.vendorPoId === vendorPoId : x.number === vendorPoId));
  if (!v) return h("div", { class: "page" }, h("p", { class: "lede" }, "That vendor PO isn't on this order."));
  // Its emails (pending, sent, discarded): approved but not sent, the pending one is the next step; once sent, what went
  // and to whom, from the guard's log.
  // (asked whatever its status: a withdrawn PO is awaiting approval again but keeps its old emails)
  const emails = can(me, "drafts.read") && v.status !== "DRAFT"
    ? (await api.tool<Page<Draft>>("outbound_drafts", { vendor_po: v.number, limit: 50 })).rows : [];
  const pendingEmail = v.status === "APPROVED" ? emails.find((d) => d.status === "PENDING") : undefined;
  const sent = sentEmailFor(v.number, emails);
  const sentLog = sent
    ? (await api.tool<Page<Partial<OutboundLogRow>>>("outbound_log", { draft_id: sent.draftId, limit: 10 })).rows.map(logRow)
        .find((r) => r.decision === "SENT" || r.decision === "REDIRECTED")
    : undefined;

  const sell = new Map(order.lines.map((l) => [l.sku, l.unitPrice]));
  // Without costs.read the answer carries no unitCost: the screen then shows quantities and price only.
  const costed = v.lines.every((l) => typeof l.unitCost === "number");
  const lines = v.lines.map((l) => {
    const price = sell.get(l.sku);
    const cost = l.qtyEa * (l.unitCost ?? 0);
    const revenue = price === undefined ? undefined : l.qtyEa * price;
    return { ...l, price, cost, revenue };
  });
  const cost = lines.reduce((s, l) => s + l.cost, 0);
  const revenue = lines.every((l) => l.revenue !== undefined) ? lines.reduce((s, l) => s + (l.revenue ?? 0), 0) : undefined;
  const gm = (c: number, r: number | undefined) => (r ? `${(((r - c) / r) * 100).toFixed(1)}%` : "");
  const supplier = v.supplier?.name ?? "the supplier";
  const awaiting = v.status === "AWAITING_APPROVAL";
  const canApprove = can(me, "vendor_pos.approve");

  const result = h("div", { class: "result", "aria-live": "polite" });
  const approve = confirmButton(`Approve ${v.number}`, `Press again to approve ${v.number}`, async () => {
    approve.disabled = true;
    try {
      const r = await api.tool<ActResult & { vendorPoId?: number }>("approve_vendor_po", { vendor_po_id: v.vendorPoId });
      const draftId = r.emailDraft?.draftId;
      // The tool's note is written for Claude ("send it with send_draft"); the app says it in its own words.
      replace(result, r.status === "APPLIED"
        ? h("p", { class: "ok" }, draftId
            ? `Approved. The email to ${supplier}, with ${v.number} attached, is ready as a draft: check it, then send it.`
            : `Approved, but the email to ${supplier} couldn't be prepared${r.emailDraft?.problem ? `: ${plain(r.emailDraft.problem)}` : ""}. ` +
              "Nothing will go until it is: once that's fixed, prepare the email again (or withdraw the approval).")
        : outcome(r, "Approved."), draftId
        ? h("a", { class: "button button-primary", href: `/draft/${draftId}` }, `Open the email to ${supplier}`)
        : r.status === "APPLIED" ? reloadButton("Show the options") : null);
      if (r.status !== "APPLIED") approve.disabled = false;
      else approve.remove();
    } catch (err) {
      replace(result, h("p", { class: "error" }, (err as ApiError).message));
      approve.disabled = false;
    }
  }, "button-primary");
  const reloadButton = (text: string) => {
    const b = h("button", { type: "button", class: "button" }, text);
    b.addEventListener("click", () => reload());
    return b;
  };
  // Approved but not gone to the supplier: the approval can be withdrawn, with a reason (withdraw_approval). The
  // database refuses it once the PO or its email has been sent.
  const canWithdraw = v.status === "APPROVED" && !v.sentAt && canApprove;
  const why = h("input", { id: "withdraw-why", type: "text", maxlength: 500, placeholder: "Why", autocomplete: "off" });
  const withdraw = confirmButton("Withdraw the approval", `Press again to withdraw ${v.number}'s approval`, async () => {
    withdraw.disabled = true;
    try {
      const r = await api.tool<ActResult>("withdraw_approval", { vendor_po_id: v.vendorPoId, note: why.value.trim() });
      if (r.status === "APPLIED") return reload(); // awaiting approval again
      replace(result, outcome(r, "Withdrawn."));
    } catch (err) {
      replace(result, h("p", { class: "error" }, (err as ApiError).message));
    }
    withdraw.disabled = false;
  }, "button-quiet", () => {
    if (why.value.trim()) return true;
    replace(result, h("p", { class: "error" }, "Say why you're withdrawing it, then withdraw."));
    return false;
  });
  const withdrawPanel = h("div", { class: "field" }, h("label", { for: "withdraw-why" }, "Withdraw the approval, with a reason"), why, withdraw,
    h("span", { class: "caption" }, `It goes back to awaiting approval and the unsent email to ${supplier} is discarded.`));

  // The PDF shows the supplier's cost, so it needs costs.read as well.
  const pdf = can(me, "costs.read") ? h("button", { type: "button", class: "button" }, "Open the PDF") : null;
  pdf?.addEventListener("click", () => openPdf(api, { vendor_po_id: v.vendorPoId }, result));

  return h("div", { class: "page" },
    awaiting
      ? crumbs({ text: "Home", href: "/" }, { text: "Vendor POs to approve" }, { text: v.number, num: true })
      : crumbs(...orderCrumbs(order.customerPoId, order.poNumber, order.customer?.name), { text: v.number, num: true }),
    h("div", { class: "stack-tight" },
      label(`Vendor PO · ${v.status.toLowerCase().replace(/_/g, " ")}`),
      h("h1", { class: "title" }, `${v.number} to ${supplier}`),
      h("p", { class: "lede" }, `For ${order.customer?.name ?? "the customer"}'s `,
        h("a", { class: "num", href: `/order/${order.customerPoId}` }, order.poNumber),
        v.requestedDelivery ? `, requested for ${longDate(v.requestedDelivery)}.` : ".")),
    view.other ? modeNotice(view.mode, me.mode) : null,
    order.orderMode === "SIMULATION"
      ? h("div", { class: "notice" }, "A simulation copy. Approving records an approval and prepares an email that would go only to the simulation recipients, marked [SIM].")
      : null,
    h("div", { class: "layout" },
      h("div", { class: "main" },
        h("section", { class: "stack", "aria-labelledby": "lines" },
          h("div", { class: "section-head" }, h("h2", { id: "lines", class: "heading" }, "What we're buying"),
            h("span", { class: "note" }, "Internal: never in anything a customer sees")),
          h("div", { class: "panel scroll" }, h("table", { class: "tbl" },
            h("thead", null, h("tr", null,
              h("th", { scope: "col" }, "SKU"), h("th", { scope: "col" }, `${supplier} part`), h("th", { scope: "col", class: "r" }, "Qty"),
              costed ? h("th", { scope: "col", class: "r" }, "Cost / EA") : null, h("th", { scope: "col", class: "r" }, "Sell / EA"),
              costed ? [h("th", { scope: "col", class: "r" }, "Cost"), h("th", { scope: "col", class: "r" }, "GM")] : null)),
            h("tbody", null, lines.map((l) => h("tr", null,
              h("td", { class: "num" }, l.sku), h("td", { class: "num muted" }, l.vendorPartNo ?? ""),
              h("td", { class: "num r" }, qty(l.qtyEa)), costed ? h("td", { class: "num r" }, unitPrice(l.unitCost)) : null,
              h("td", { class: "num r" }, unitPrice(l.price)),
              costed ? [h("td", { class: "num r" }, money(l.cost)), h("td", { class: "num r" }, gm(l.cost, l.revenue))] : null))),
            costed
              ? h("tfoot", null, h("tr", null, h("td", { colspan: 5 }, "Total"), h("td", { class: "num r" }, money(cost)),
                  h("td", { class: "num r" }, gm(cost, revenue))))
              : null))),
        order.notes ? h("section", { class: "stack" }, h("h2", { class: "heading" }, "Notes"), h("p", { class: "prose" }, order.notes)) : null,
        historySection(forVendorPo(orderHistory, v.number), "Its approval, its email and what happens next are recorded here.")),
      h("aside", { class: "side", "aria-label": "Decision" },
        h("div", { class: "panel decision" },
          costed ? h("div", { class: "row" }, h("span", null, "Cost"), h("span", { class: "num" }, money(cost))) : null,
          revenue !== undefined ? h("div", { class: "row" }, h("span", null, "Sell"), h("span", { class: "num" }, money(revenue))) : null,
          costed && revenue !== undefined
            ? h("div", { class: "row total" }, h("span", null, "Gross margin"), h("span", { class: "num ink" }, `${money(revenue - cost)} · ${gm(cost, revenue)}`))
            : null,
          awaiting && canApprove ? approve : null,
          awaiting && !canApprove ? pill("Waiting for someone who approves POs", "soon") : null,
          !awaiting ? pill(v.status.toLowerCase().replace(/_/g, " "), v.approvedAt ? "good" : "neutral") : null,
          pendingEmail
            ? h("a", { class: "button button-primary", href: `/draft/${pendingEmail.draftId}` }, `Open the email to ${supplier}`)
            : v.status === "APPROVED" && !v.sentAt
              ? [h("p", { class: "notice" }, `No email to ${supplier} is waiting, so nothing will go until one is prepared and sent.`),
                 can(me, "drafts.write") ? prepareEmailButton(api, v.vendorPoId, result, "button-primary") : null]
              : null,
          canWithdraw ? withdrawPanel : null,
          pdf,
          h("p", { class: "caption" }, `Approving doesn't send it. It prepares the email to ${supplier}, with the PO attached, ` +
            "as a draft; a person sends it through the outbound guard. Recorded under your name."),
          result),
        sent ? sentPanel(sent, sentLog, supplier) : null,
        emails.length ? emailsPanel(emails) : null,
        h("div", { class: "panel facts" },
          label("Dates"),
          h("span", null, `Customer PO ${shortDate(order.poDate)}`, v.orderedOn ? ` · vendor PO ${shortDate(v.orderedOn)}` : "",
            v.requestedDelivery ? ` · requested ${shortDate(v.requestedDelivery)}` : "")))));
}
